import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const initialForm = {
  maintenanceType: "BREAKDOWN",
  serviceDate: new Date().toISOString().split("T")[0],
  issueDescription: "",
  workDone: "",
  partsReplaced: "",
  repairCost: "",
  vendorName: "",
  slaTarget: 3,
};

const dropStyle = { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px", maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" };

export default function NewMaintenance() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const [allAssets, setAllAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState("");
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetDrop, setShowAssetDrop] = useState(false);
  const assetRef = useRef(null);

  const [allVendors, setAllVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorDrop, setShowVendorDrop] = useState(false);
  const vendorRef = useRef(null);

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/assets?page=0&size=200`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { const l = Array.isArray(d) ? d : d.content || []; setAllAssets(l); setFilteredAssets(l); }).catch(() => {});
    fetch(`${API_BASE_URL}/api/vendors`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { const l = Array.isArray(d) ? d : []; setAllVendors(l); setFilteredVendors(l); }).catch(() => {});
  }, [token]);

  useEffect(() => {
    const q = assetSearch.trim().toLowerCase();
    if (!q) { setFilteredAssets(allAssets); return; }
    setFilteredAssets(allAssets.filter(a =>
      (a.assetName || "").toLowerCase().includes(q) || (a.assetCode || "").toLowerCase().includes(q) ||
      (a.serialNumber || "").toLowerCase().includes(q) || (a.brand || "").toLowerCase().includes(q)
    ));
  }, [assetSearch, allAssets]);

  useEffect(() => {
    const q = vendorSearch.trim().toLowerCase();
    if (!q) { setFilteredVendors(allVendors); return; }
    setFilteredVendors(allVendors.filter(v =>
      (v.name || "").toLowerCase().includes(q) || (v.contactPerson || "").toLowerCase().includes(q) || (v.phone || "").toLowerCase().includes(q)
    ));
  }, [vendorSearch, allVendors]);

  useEffect(() => {
    const handler = e => {
      if (assetRef.current && !assetRef.current.contains(e.target)) setShowAssetDrop(false);
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setShowVendorDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedAsset) { toast.error("Please select an asset."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-maintenance`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ assetId: selectedAsset.id, ...formData }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Maintenance request raised successfully.");
      navigate("/allmaintenance");
    } catch (err) { toast.error(err.message || "Failed to raise maintenance request."); }
    finally { setSubmitting(false); }
  };

  const handleClear = () => {
    setFormData(initialForm);
    setSelectedAsset(null); setAssetSearch("");
    setSelectedVendor(null); setVendorSearch("");
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-tools me-2"></i> Raise Maintenance Request</h4>
            <div className="d-flex gap-2 mt-1 mb-1">
            <button className="btn btn-primary" type="submit" form="maintenance-form" disabled={submitting}>
              {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Submitting…</> : "Save"}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>Cancel</button>
          </div>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>Register a service issue, track downtime, and maintain vendor repair history.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allmaintenance")}>
              <i className="bi bi-list-ul me-1"></i> Maintenance List
            </button>
          </div>
        </div>

        <form id="maintenance-form" onSubmit={handleSubmit}>
          <Section title="Select Asset">
            <div className="col-md-5">
              <label className="form-label fw-semibold">Asset <span className="text-danger">*</span></label>
              <div className="position-relative" ref={assetRef}>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-laptop" /></span>
                  <input className="form-control" placeholder="Search by code, name, brand…"
                    value={assetSearch}
                    onChange={e => { setAssetSearch(e.target.value); setSelectedAsset(null); setShowAssetDrop(true); }}
                    onFocus={() => setShowAssetDrop(true)} autoComplete="off" />
                  {selectedAsset && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setSelectedAsset(null); setAssetSearch(""); }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
                {showAssetDrop && filteredAssets.length > 0 && (
                  <div style={dropStyle}>
                    {filteredAssets.slice(0, 30).map(a => (
                      <div key={a.id} style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        onMouseDown={() => { setSelectedAsset(a); setAssetSearch(`${a.assetCode} – ${a.assetName}`); setShowAssetDrop(false); }}>
                        <span className="fw-semibold text-primary" style={{ fontFamily: "monospace" }}>{a.assetCode}</span>
                        <span className="ms-2">{a.assetName}</span>
                        <span className="ms-2 text-muted" style={{ fontSize: 11 }}>{a.category}{a.brand ? ` · ${a.brand}` : ""}</span>
                        <span className={`badge ms-2 ${a.status === "Available" ? "bg-success" : a.status === "In Repair" ? "bg-warning text-dark" : "bg-secondary"}`} style={{ fontSize: 10 }}>{a.status}</span>
                      </div>
                    ))}
                    {filteredAssets.length > 30 && <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>+ {filteredAssets.length - 30} more — type to narrow</div>}
                  </div>
                )}
                {showAssetDrop && filteredAssets.length === 0 && assetSearch && (
                  <div style={dropStyle}><div className="text-muted text-center py-3" style={{ fontSize: 13 }}>No assets found</div></div>
                )}
              </div>
            </div>
            {selectedAsset && (
              <>
                <div className="col-md-2">
                  <label className="form-label">Current Status</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.status || "—"} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Category</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.category || "—"} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Location</label>
                  <input className="form-control bg-light" readOnly value={selectedAsset.location || "—"} />
                </div>
              </>
            )}
          </Section>

          <Section title="Maintenance Details">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Maintenance Type</label>
              <select name="maintenanceType" className="form-select" value={formData.maintenanceType} onChange={handleChange}>
                <option value="BREAKDOWN">BREAKDOWN</option>
                <option value="PREVENTIVE">PREVENTIVE</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Service Date</label>
              <input type="date" name="serviceDate" className="form-control" value={formData.serviceDate} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">Vendor</label>
              <div className="position-relative" ref={vendorRef}>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-shop" /></span>
                  <input className="form-control" placeholder="Search vendor by name or contact…"
                    value={vendorSearch}
                    onChange={e => { setVendorSearch(e.target.value); setSelectedVendor(null); setFormData(p => ({ ...p, vendorName: e.target.value })); setShowVendorDrop(true); }}
                    onFocus={() => setShowVendorDrop(true)} autoComplete="off" />
                  {selectedVendor && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => { setSelectedVendor(null); setVendorSearch(""); setFormData(p => ({ ...p, vendorName: "" })); }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
                {showVendorDrop && filteredVendors.length > 0 && (
                  <div style={dropStyle}>
                    {filteredVendors.slice(0, 20).map(v => (
                      <div key={v.id} style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        onMouseDown={() => { setSelectedVendor(v); setVendorSearch(v.name); setFormData(p => ({ ...p, vendorName: v.name })); setShowVendorDrop(false); }}>
                        <span className="fw-semibold">{v.name}</span>
                        {v.contactPerson && <span className="ms-2 text-muted" style={{ fontSize: 12 }}>· {v.contactPerson}</span>}
                        {v.phone && <span className="ms-2 text-muted" style={{ fontSize: 12 }}>· {v.phone}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {showVendorDrop && filteredVendors.length === 0 && (
                  <div style={dropStyle}><div className="text-muted text-center py-3" style={{ fontSize: 13 }}>No vendors found — you can still type a name manually</div></div>
                )}
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">SLA Target (days)</label>
              <input type="number" name="slaTarget" className="form-control" value={formData.slaTarget} onChange={handleChange} min="1" />
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold">Issue Description</label>
              <textarea name="issueDescription" className="form-control" rows={3} value={formData.issueDescription} onChange={handleChange} placeholder="Describe the problem in detail…" />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Work Done</label>
              <textarea name="workDone" className="form-control" rows={3} value={formData.workDone} onChange={handleChange} placeholder="Describe what was done to fix the issue…" />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Parts Replaced</label>
              <input type="text" name="partsReplaced" className="form-control" value={formData.partsReplaced} onChange={handleChange} placeholder="e.g. Battery, RAM" />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Repair Cost (₹)</label>
              <input type="number" name="repairCost" className="form-control" value={formData.repairCost} onChange={handleChange} min="0" placeholder="0" />
            </div>
          </Section>

          <div className="d-flex gap-2 mt-3 mb-4">
            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Submitting…</> : "Save"}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}
